// Hand-written typed client for the /api/v1/api-tokens endpoints.
//
// This mirrors what `npm run generate-client` produces from the OpenAPI spec
// (an `ApiTokensService` class plus a few request/response types). It is kept
// as a separate module so once the user regenerates the OpenAPI client, the
// generated `ApiTokensService` from `@/client` can replace this file
// transparently — the consumers (api-tokens.tsx page, queries) only depend on
// the named exports below.

import type { CancelablePromise } from "@/client/core/CancelablePromise"
import { OpenAPI } from "@/client/core/OpenAPI"
import { request as __request } from "@/client/core/request"

// Mirrors backend `app.models.auth.ResourceType` (string enum).
export type ResourceType =
  | "organisation"
  | "user"
  | "reader"
  | "tag"
  | "tag_template"
  | "tag_reading"
  | "tag_event"
  | "session"
  | "item"

// Bitmask values from `app.models.auth.Permission` (Flag enum).
export const PermissionFlag = {
  NONE: 0,
  READ: 1,
  CREATE: 2,
  UPDATE: 4,
  DELETE: 8,
} as const
export type PermissionFlag =
  (typeof PermissionFlag)[keyof typeof PermissionFlag]

export interface ApiTokenScope {
  resource_type: ResourceType
  /** Bitmask of `PermissionFlag` values (e.g. 1 = READ, 3 = READ|CREATE). */
  permission: number
}

export interface ApiTokenPublic {
  id: string
  organisation_id: string
  name: string
  token_prefix: string
  scopes: ApiTokenScope[]
  expires_at: string | null
  last_used_at: string | null
  created_at: string
}

export interface ApiTokensPublic {
  data: ApiTokenPublic[]
  count: number
}

export interface ApiTokenCreate {
  name: string
  organisation_id: string
  /** ``null`` means the token never expires. */
  expires_in_days: number | null
  scopes: ApiTokenScope[]
}

export interface ApiTokenCreateResponse extends ApiTokenPublic {
  /** Plaintext token returned exactly once at creation. */
  token: string
}

export interface ApiTokensListData {
  organisationId?: string
}

// Static-only class on purpose — it mirrors the shape `@hey-api/openapi-ts`
// produces (`SomethingService.method(...)`), so when the OpenAPI client is
// regenerated this hand-written stub can be deleted as a drop-in swap.
// biome-ignore lint/complexity/noStaticOnlyClass: matches generated client convention
export class ApiTokensService {
  static createApiToken(data: {
    requestBody: ApiTokenCreate
  }): CancelablePromise<ApiTokenCreateResponse> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/api-tokens/",
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        400: "Bad Request",
        401: "Unauthorized",
        403: "Forbidden",
        422: "Validation Error",
      },
    })
  }

  static listApiTokens(
    data: ApiTokensListData = {},
  ): CancelablePromise<ApiTokensPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/api-tokens/",
      query: { organisation_id: data.organisationId },
      errors: { 401: "Unauthorized" },
    })
  }

  static revokeApiToken(data: {
    tokenId: string
  }): CancelablePromise<{ message: string }> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/api/v1/api-tokens/{token_id}",
      path: { token_id: data.tokenId },
      errors: { 401: "Unauthorized", 404: "Not Found" },
    })
  }
}
